import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { 
  ArrowLeft, 
  RefreshCw, 
  User, 
  BookOpen, 
  Calculator,
  CheckCircle,
  AlertCircle
} from 'lucide-react';
import useFetch from '../constants/useFetch';
import { API_BASE_URL } from '../constants';

const EnterScores = () => {
  const { studentUuid, classUuid, subjectId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useSelector((state) => state.auth);
  const { teacherData } = useSelector((state) => state.teacherData || {});

//   // Get class info from URL params
//   const currentClass = teacherData?.classrooms?.find(cls => cls.uuid === classUuid);
  
//   // Find student from Redux data
//   const student = teacherData?.students?.find(s => s.uuid === studentUuid);
//   console.log({teacherData});

  // Fetch latest result for the student in this classroom
//   const resultUrl = `${API_BASE_URL}/api/web/result/get-student-subject-result/${studentUuid}/${currentClass?.class_name}`;
  const resultUrl = `${API_BASE_URL}/api/web/result/get-student-subject-result/${studentUuid}/${classUuid}/${subjectId}`;
  const { data: latestResult, loading: resultLoading, error: fetchError } = useFetch(resultUrl);

  // Get class info from URL params
  const currentClass = teacherData?.classrooms?.find(cls => cls.uuid === classUuid) || latestResult?.data?.classroom || {};
  
  // Find student from Redux data
  const student = teacherData?.students?.find(s => s.uuid === studentUuid) || latestResult?.data?.student || {};
  // console.log({teacherData});
  
  const [formData, setFormData] = useState({
    ca1: '',
    ca2: '',
    assignment: '',
    project: '',
    exam: ''
  });

  const [isFormValid, setIsFormValid] = useState(false);
  const [error, setError] = useState('');
  const [isSubmittingResult, setIsSubmittingResult] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  // console.log({latestResultData: latestResult?.data?.subject?.subject_name});
  

  // Load existing result data
  useEffect(() => {
    if (latestResult?.data) {
      setFormData({
        ca1: latestResult?.data?.existingResult?.test1 || '',
        ca2: latestResult?.data?.existingResult?.test2 || '',
        assignment: latestResult?.data?.existingResult?.assignment || '',
        project: latestResult?.data?.existingResult?.project || '',
        exam: latestResult?.data?.existingResult?.exam || ''
      });
    }
  }, [latestResult]);

  // Validate individual field
  const validateField = (name, value) => {
    if (value === '') return true;
    const numValue = Number(value);
    if (isNaN(numValue) || numValue < 0) return false;
    
    if (name === 'exam') {
      return numValue <= 60;
    }
    return numValue <= 10;
  };

  // Handle form field changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    if (validateField(name, value)) {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
      setError('');
      setSuccessMessage('');
    } else {
      setError(`Invalid value for ${name}. ${name === 'exam' ? 'Must be between 0 and 60' : 'Must be between 0 and 10'}`);
    }
  };

  // Validate form on data change
  useEffect(() => {
    const hasAtLeastOneValue = Object.values(formData).some(value => value !== '');
    const allValuesValid = Object.entries(formData).every(([name, value]) => 
      value === '' || validateField(name, value)
    );
    setIsFormValid(hasAtLeastOneValue && allValuesValid);
  }, [formData]);

  // Utility function to format decimal values
  const formatScore = (value) => {
    // console.log({value});
    
    if (value === undefined || value === null || value === '') return '';
    
    const num = parseFloat(value);
    if (isNaN(num)) return '';
    
    // If the number ends with .0, show as integer
    if (num % 1 === 0) {
      return Math.floor(num).toString();
    }
    
    // Otherwise, show as is
    return num.toString();
  };

  // Calculate total score
  const calculateTotal = () => {
    const ca1 = Number(formData.ca1) || 0;
    const ca2 = Number(formData.ca2) || 0;
    const assignment = Number(formData.assignment) || 0;
    const project = Number(formData.project) || 0;
    const exam = Number(formData.exam) || 0;
    
    return ca1 + ca2 + assignment + project + exam;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isFormValid) return;
    
    setIsSubmittingResult(true);
    setError('');
    setSuccessMessage('');
    
    try {
      const response = await fetch(`${API_BASE_URL}/api/web/result/save-single`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${user?.token || localStorage.getItem('authToken')}`
        },
        body: JSON.stringify({
          student_id: student.id,
          teacher_id: user.id,
          classroom_slug: currentClass?.class_name?.toLowerCase() || latestResult?.data?.classroom?.class_name?.toLowerCase(),
          subject_slug: latestResult?.data?.subject?.slug,
          session: user?.currentSession, // You might want to make this dynamic
          term: user?.currentTerm, // You might want to make this dynamic
          test1: parseFloat(formData.ca1) || 0,
          test2: parseFloat(formData.ca2) || 0,
          assignment: parseFloat(formData.assignment) || 0,
          project: parseFloat(formData.project) || 0,
          exam: parseFloat(formData.exam) || 0,
        })
      });

      if (!response.ok) {
        throw new Error('Failed to save result');
      }

      const result = await response.json();
      
      // Update form with saved data
      setFormData({
        ca1: result.data?.test1 || '',
        ca2: result.data?.test2 || '',
        assignment: result.data?.assignment || '',
        project: result.data?.project || '',
        exam: result.data?.exam || ''
      });
      
      setSuccessMessage(`Score for ${student?.firstname} ${student?.lastname} saved successfully!`);
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccessMessage(''), 3000);
      
    } catch (err) {
      setError('Failed to save result. Please try again.');
    } finally {
      setIsSubmittingResult(false);
    }
  };

  // Loading state
  if (resultLoading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="w-8 h-8 text-blue-500 animate-spin mx-auto mb-4" />
          <p className="text-gray-600 dark:text-gray-400">Loading result information...</p>
        </div>
      </div>
    );
  }

  // Student not found
  if (!student) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-6">
        <div className="max-w-2xl mx-auto">
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-600 dark:text-red-400 p-4 rounded-lg">
            <div className="flex items-center">
              <AlertCircle className="w-5 h-5 mr-2" />
              Student information not found. Please go back and try again.
            </div>
          </div>
          <button
            onClick={() => navigate(-1)}
            className="mt-4 flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 shadow-sm border-b border-gray-200 dark:border-gray-700">
        <div className="max-w-4xl mx-auto px-6 py-4">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                Enter {latestResult?.data?.subject?.subject_name || 'Subject'} Score
              </h1>
              <p className="text-gray-600 dark:text-gray-400 mt-1">
                {student?.firstname} {student?.lastname} - {latestResult?.data?.classroom?.class_name}
              </p>
            </div>
            <button
              onClick={() => navigate(-1)}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Back
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-6 py-8">
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
          {/* Student Info Card */}
          <div className="p-6 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center space-x-4">
              <div className="relative">
                {student.profile_picture ? (
                  <img
                    src={student.profile_picture}
                    alt={`${student.firstname} ${student.lastname}`}
                    className="w-16 h-16 rounded-full object-cover border-2 border-blue-100 dark:border-blue-900"
                  />
                ) : (
                  <div className="w-16 h-16 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-full flex items-center justify-center border-2 border-blue-100 dark:border-blue-900">
                    <User className="w-8 h-8 text-white" />
                  </div>
                )}
              </div>
              <div>
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                  {student.firstname} {student.lastname}
                </h2>
                <p className="text-gray-600 dark:text-gray-400">
                  {student.admission_no} • {latestResult?.data?.classroom?.class_name}
                </p>
              </div>
            </div>
          </div>

          {/* Form */}
          <div className="p-6">
            {fetchError && (
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-600 dark:text-red-400 p-4 rounded-lg mb-6">
                <div className="flex items-center">
                  <AlertCircle className="w-5 h-5 mr-2" />
                  Error loading previous result. Please try again.
                </div>
              </div>
            )}

            {error && (
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-600 dark:text-red-400 p-4 rounded-lg mb-6">
                <div className="flex items-center">
                  <AlertCircle className="w-5 h-5 mr-2" />
                  {error}
                </div>
              </div>
            )}

            {successMessage && (
              <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 text-green-600 dark:text-green-400 p-4 rounded-lg mb-6">
                <div className="flex items-center">
                  <CheckCircle className="w-5 h-5 mr-2" />
                  {successMessage}
                </div>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Score Input Fields */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                <div>
                  <label htmlFor="ca1" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    CA1 (0-10)
                  </label>
                  <input
                    type="number"
                    id="ca1"
                    name="ca1"
                    // value={formData.ca1}
                    value={formatScore(formData.ca1)}
                    onChange={handleChange}
                    min="0"
                    max="10"
                    step="0.5"
                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white transition-colors"
                    placeholder="0"
                  />
                </div>

                <div>
                  <label htmlFor="ca2" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    CA2 (0-10)
                  </label>
                  <input
                    type="number"
                    id="ca2"
                    name="ca2"
                    value={formatScore(formData.ca2)}
                    onChange={handleChange}
                    min="0"
                    max="10"
                    step="0.5"
                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white transition-colors"
                    placeholder="0"
                  />
                </div>

                <div>
                  <label htmlFor="assignment" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Assignment (0-10)
                  </label>
                  <input
                    type="number"
                    id="assignment"
                    name="assignment"
                    // value={formData.assignment}
                    value={formatScore(formData.assignment)}
                    onChange={handleChange}
                    min="0"
                    max="10"
                    step="0.5"
                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white transition-colors"
                    placeholder="0"
                  />
                </div>

                <div>
                  <label htmlFor="project" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Project (0-10)
                  </label>
                  <input
                    type="number"
                    id="project"
                    name="project"
                    // value={formData.project}
                    value={formatScore(formData.project)}
                    onChange={handleChange}
                    min="0"
                    max="10"
                    step="0.5"
                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white transition-colors"
                    placeholder="0"
                  />
                </div>

                <div>
                  <label htmlFor="exam" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Exam (0-60)
                  </label>
                  <input
                    type="number"
                    id="exam"
                    name="exam"
                    // value={formData.exam}
                    value={formatScore(formData.exam)}
                    onChange={handleChange}
                    min="0"
                    max="60"
                    step="0.5"
                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white transition-colors"
                    placeholder="0"
                  />
                </div>
              </div>

              {/* Total Score Display */}
              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <Calculator className="w-5 h-5 text-blue-600 dark:text-blue-400 mr-2" />
                    <span className="text-sm font-medium text-blue-800 dark:text-blue-200">Total Score</span>
                  </div>
                  <div className="text-2xl font-bold text-blue-900 dark:text-blue-100">
                    {calculateTotal()}/100
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex justify-end gap-4 pt-6 border-t border-gray-200 dark:border-gray-700">
                <button
                  type="button"
                  onClick={() => navigate(-1)}
                  className="px-6 py-3 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={!isFormValid || isSubmittingResult}
                  className={`px-6 py-3 rounded-lg transition-colors flex items-center gap-2 ${
                    isFormValid && !isSubmittingResult
                      ? 'bg-blue-600 text-white hover:bg-blue-700'
                      : 'bg-gray-300 dark:bg-gray-600 text-gray-500 dark:text-gray-400 cursor-not-allowed'
                  }`}
                >
                  {isSubmittingResult ? (
                    <>
                      <RefreshCw className="w-4 h-4 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <BookOpen className="w-4 h-4" />
                      Save Result
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EnterScores; 